/**
 * Public Payment Service
 * Fatura ödeme API çağrıları için service
 */

/**
 * Component/service asla backend'e direkt istek atmamalı.
 * Tüm istekler Next.js API route'larına (/api/...) gider; API route backend'e proxy eder.
 */
const getApiUrl = (): string => {
  if (typeof window !== 'undefined') {
    return ''; // Relative URL → aynı origin → Next.js /api/* route'ları
  }
  // Server-side (SSR): Next.js uygulamasının kendi API'sine git, backend'e değil
  return process.env.NEXT_PUBLIC_SITE_URL || 'http://localhost:3000';
};

export interface CardCommissionQuote {
  amount: number;
  commission_rate: number;
  commission_amount: number;
  total_with_commission: number;
}

/** Book Now / ödeme özeti: kart komisyonu (tek çekim oranı) */
export async function fetchCardCommissionQuote(amount: number): Promise<CardCommissionQuote | null> {
  if (!(amount > 0)) return null;
  try {
    const params = new URLSearchParams({ amount: String(amount) });
    const res = await fetch(`/api/pay/card-commission?${params.toString()}`, { credentials: 'include' });
    const data = await res.json();
    if (!res.ok) return null;
    const payload = (data?.data ?? data) as Partial<CardCommissionQuote>;
    if (typeof payload.total_with_commission !== 'number') return null;
    return {
      amount: typeof payload.amount === 'number' ? payload.amount : amount,
      commission_rate: typeof payload.commission_rate === 'number' ? payload.commission_rate : 0,
      commission_amount: typeof payload.commission_amount === 'number' ? payload.commission_amount : 0,
      total_with_commission: payload.total_with_commission,
    };
  } catch {
    return null;
  }
}

// Types
export interface InvoiceItem {
  description: string;
  quantity: number;
  unit_price: number;
  tax_rate: number;
  tax_amount: number;
  discount_type: 'percentage' | 'fixed' | null;
  discount_value: number;
  discount_amount: number;
  total_amount: number;
}

export interface InvoiceData {
  id: number;
  invoice_no: string;
  status: string;
  customer_name: string;
  customer_email: string;
  /** Fatura bilgisi (adres, vergi no, vergi dairesi) tam mı? */
  billing_info_complete?: boolean;
  /** Eksik fatura bilgisi alanları */
  missing_billing_fields?: string[];
  total_amount: number;
  tax_amount: number;
  paid_amount: number;
  remaining_amount: number;
  currency: {
    code: string;
    symbol: string;
  };
  issue_date: string;
  due_date: string;
  items: InvoiceItem[];
  /** Müşteriye ait kayıtlı fatura adresleri (ödemede seçilebilir) */
  customer_billing_addresses?: Array<{
    id: number;
    type: string;
    title?: string | null;
    is_default: boolean;
    first_name?: string;
    last_name?: string;
    company_name?: string;
    tax_number?: string;
    tax_office?: string;
    address?: string;
    city?: string;
    country?: string;
  }>;
  /** Rezervasyona ait özet (paket, tarihler, yolcu sayısı) */
  reservation_summary?: {
    package_name?: string | null;
    product_name?: string | null;
    check_in_date?: string | null;
    check_out_date?: string | null;
    travel_date?: string | null;
    guest_count?: number;
    /** Oda bazlı yolcu: string (eski API) veya yapılandırılmış (çeviri için) */
    rooms_guest_summary?: string[] | Array<{ room_number: number; adults: number; children: number }>;
    is_tour_or_ship?: boolean;
  };
}

export interface PaymentMethod {
  key: string;
  name: string;
  icon: string;
  supports_installment: boolean;
  supports_3d: boolean;
}

export interface BankAccount {
  id: number;
  bank_name: string;
  account_name: string;
  iban: string;
  swift_code?: string;
  currency_code: string;
  logo?: string;
}

export interface InvoiceResponse {
  invoice: InvoiceData;
  payable: boolean;
  message?: string;
  payment_methods?: PaymentMethod[];
  bank_accounts?: BankAccount[];
  test_mode?: boolean;
  /** Ödeme sağlayıcı modül anahtarı (örn. garanti_pos, iyzico) - test kartı dropdown için */
  payment_provider_key?: string;
  /** Taksit seçenekleri (aktif sanal pos ile aynı yanıtta; ek istek gerekmez) */
  installments?: InstallmentOption[];
  partial_payment?: import('@/types/partial-payment').PartialPaymentContext;
  payment_schedule?: Array<{
    id: number;
    amount: number;
    late_fee_amount?: number;
    total_due?: number;
    due_date?: string | null;
    status?: string;
    payment_url?: string | null;
  }>;
  site?: {
    name: string;
    logo?: string;
  };
}

export interface InstallmentOption {
  installmentNumber: number;
  installmentPrice: number;
  totalPrice: number;
}

export interface InstallmentResponse {
  card_info?: {
    card_type: string;
    card_family: string;
    bank_name: string;
  };
  installments: InstallmentOption[];
}

export interface CardPaymentData {
  card_holder_name: string;
  card_number: string;
  expire_month: string;
  expire_year: string;
  cvc: string;
  installment?: number;
  use_3d?: boolean;
  /** Kısmi ödeme tutarı (yoksa fatura kalan tutarı) */
  amount?: number;
  /** Müşteri maili için dil (tr, en vb.); gönderilmezse site varsayılanı kullanılır */
  locale?: string;
}

export interface CardPaymentResponse {
  success: boolean;
  requires_3d?: boolean;
  html_content?: string;
  payment_id?: number;
  transaction_id?: string;
  message?: string;
}

export interface BillingInfoData {
  billing_address_id?: number;
  billing_type?: 'individual' | 'corporate';
  customer_address?: {
    address?: string;
    city?: string;
    country?: string;
    postal_code?: string;
    first_name?: string;
    last_name?: string;
    company_name?: string;
  };
  customer_tax_number?: string;
  customer_tax_office?: string;
}

export interface BankTransferData {
  bank_account_id: number;
  amount: number;
  transfer_date: string;
  sender_name: string;
  sender_bank?: string;
  sender_iban?: string;
  reference_no?: string;
  sender_email: string;
  sender_phone?: string;
  notes?: string;
}

export interface PaymentStatusResponse {
  invoice_no: string;
  status: string;
  total_amount: number;
  paid_amount: number;
  remaining_amount: number;
  is_paid: boolean;
  currency: string;
}

class PaymentService {
  private baseUrl: string;

  constructor() {
    this.baseUrl = getApiUrl();
  }

  /**
   * Fatura bilgisini getir
   */
  async getInvoice(token: string): Promise<InvoiceResponse> {
    const response = await fetch(`${this.baseUrl}/api/pay/${token}`, {
      method: 'GET',
      headers: {
        'Accept': 'application/json',
        'Content-Type': 'application/json',
      },
    });

    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.message || 'Fatura bilgisi alınamadı');
    }

    return data.data;
  }

  /**
   * Fatura bilgisini güncelle (adres, vergi no, vergi dairesi)
   */
  async updateBillingInfo(token: string, billingData: BillingInfoData): Promise<{ success: boolean; invoice: InvoiceData; message: string }> {
    const response = await fetch(`${this.baseUrl}/api/pay/${token}/billing-info`, {
      method: 'PATCH',
      headers: {
        'Accept': 'application/json',
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(billingData),
    });

    const text = await response.text();
    let data: { data?: { success: boolean; invoice: InvoiceData; message: string }; message?: string };
    try {
      data = text ? JSON.parse(text) : {};
    } catch {
      throw new Error('Fatura bilgisi güncellenemedi');
    }

    if (!response.ok) {
      throw new Error(data.message || 'Fatura bilgisi güncellenemedi');
    }

    return data.data!;
  }

  /**
   * Taksit seçeneklerini getir
   */
  async getInstallments(token: string, binNumber: string, amount?: number): Promise<InstallmentResponse> {
    const response = await fetch(`${this.baseUrl}/api/pay/${token}/installments`, {
      method: 'POST',
      headers: {
        'Accept': 'application/json',
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        bin_number: binNumber,
        ...(amount != null && amount > 0 ? { amount } : {}),
      }),
    });

    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.message || 'Taksit bilgisi alınamadı');
    }

    return data.data;
  }

  /**
   * Book Now: tutar + BIN ile banka taksiti (fatura token'ı olmadan).
   */
  async getInstallmentsByAmount(binNumber: string, amount: number): Promise<InstallmentResponse> {
    const response = await fetch(`${this.baseUrl}/api/pay/installments-by-amount`, {
      method: 'POST',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
      },
      credentials: 'include',
      body: JSON.stringify({ bin_number: binNumber, amount }),
    });

    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.message || 'Taksit bilgisi alınamadı');
    }

    return data.data;
  }

  /**
   * Kredi kartı ile ödeme yap.
   * Tarayıcıda çağrıldığında callback_base_url (mevcut sayfa origin'i) eklenir; Garanti ödeme sonrası bu adrese döner, frontend sonucu fatura sayfasında gösterir.
   */
  async payWithCard(token: string, cardData: CardPaymentData): Promise<CardPaymentResponse> {
    const payload: CardPaymentData & { callback_base_url?: string } = { ...cardData };
    if (typeof window !== 'undefined' && window.location?.origin) {
      payload.callback_base_url = window.location.origin;
    }
    const response = await fetch(`${this.baseUrl}/api/pay/${token}/card`, {
      method: 'POST',
      headers: {
        'Accept': 'application/json',
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(payload),
    });

    const raw = await response.text();
    let data: Record<string, unknown> = {};
    if (raw.trim()) {
      try {
        data = JSON.parse(raw) as Record<string, unknown>;
      } catch {
        throw new Error('Ödeme servisi geçersiz yanıt döndü. Lütfen tekrar deneyin.');
      }
    } else if (!response.ok) {
      throw new Error('Ödeme işlemi başarısız');
    } else {
      throw new Error('Ödeme servisi boş yanıt döndü. Lütfen tekrar deneyin.');
    }

    if (!response.ok) {
      const msg =
        (typeof data.message === 'string' && data.message) ||
        (typeof data.error === 'string' && data.error) ||
        'Ödeme işlemi başarısız';
      const err = new Error(msg) as Error & { error_code?: string };
      if (typeof data.error_code === 'string') err.error_code = data.error_code;
      throw err;
    }

    return (data.data || data) as CardPaymentResponse;
  }

  /**
   * Banka havalesi bildirimi oluştur
   */
  async createBankTransfer(token: string, transferData: BankTransferData): Promise<{ notification_id: number; message: string }> {
    const response = await fetch(`${this.baseUrl}/api/pay/${token}/bank-transfer`, {
      method: 'POST',
      headers: {
        'Accept': 'application/json',
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(transferData),
    });

    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.message || 'Havale bildirimi oluşturulamadı');
    }

    return data.data || data;
  }

  /**
   * Dekont yükle
   */
  async uploadReceipt(token: string, notificationId: number, file: File): Promise<void> {
    const formData = new FormData();
    formData.append('receipt', file);

    const response = await fetch(`${this.baseUrl}/api/pay/${token}/bank-transfer/${notificationId}/receipt`, {
      method: 'POST',
      headers: {
        'Accept': 'application/json',
      },
      body: formData,
    });

    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.message || 'Dekont yüklenemedi');
    }
  }

  /**
   * Ödeme durumunu kontrol et
   */
  async checkStatus(token: string): Promise<PaymentStatusResponse> {
    const response = await fetch(`${this.baseUrl}/api/pay/${token}/status`, {
      method: 'GET',
      headers: {
        'Accept': 'application/json',
        'Content-Type': 'application/json',
      },
    });

    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.message || 'Durum kontrol edilemedi');
    }

    return data.data;
  }

  /**
   * 3D Secure callback işle
   * Local ortamda iyzico callback'e erişilemediği için,
   * frontend'de URL parametrelerinden yakalanan callback verilerini backend'e gönderir
   */
  async handle3DCallback(token: string, callbackParams: Record<string, string | null>): Promise<{ success: boolean; error_message?: string; message?: string }> {
    const response = await fetch(`${this.baseUrl}/api/pay/callback/3ds`, {
      method: 'POST',
      headers: {
        'Accept': 'application/json',
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        token,
        ...callbackParams,
      }),
    });

    const data = await response.json();

    if (!response.ok) {
      const msg = data.message || data.error || 'Callback işlemi başarısız';
      const err = new Error(msg) as Error & { error_code?: string };
      if (data.error_code) err.error_code = data.error_code;
      throw err;
    }

    return data.data || data;
  }

  /**
   * Kart numarasını formatla
   */
  formatCardNumber(value: string): string {
    const v = value.replace(/\s+/g, '').replace(/[^0-9]/gi, '');
    const matches = v.match(/\d{4,16}/g);
    const match = (matches && matches[0]) || '';
    const parts = [];

    for (let i = 0, len = match.length; i < len; i += 4) {
      parts.push(match.substring(i, i + 4));
    }

    if (parts.length) {
      return parts.join(' ');
    } else {
      return value;
    }
  }

  /**
   * Kart tipini belirle
   */
  getCardType(number: string): string {
    const cleaned = number.replace(/\s+/g, '');
    
    if (/^4/.test(cleaned)) return 'visa';
    if (/^5[1-5]/.test(cleaned)) return 'mastercard';
    if (/^3[47]/.test(cleaned)) return 'amex';
    if (/^9/.test(cleaned)) return 'troy';
    
    return 'unknown';
  }
}

export const paymentService = new PaymentService();
export default paymentService;
